// Статусът на отчет при разнасяне — на ДВЕ места, с една и съща логика:
//  • картата на чакащ отчет в списъка („6 продукта без склад", „2 продукта с
//    недостиг · 1 със заместител") — от прегледа, по позициите на предизбора
//    (`defaultAllocations`): продукт, разделен между Бар и Кухня, е в недостиг,
//    ако който и да е дял не стига в своя склад. Недостигът е ПРЕДИ замени —
//    това е работата, която отчетът носи, не резултатът след нея;
//  • хапчетата горе вдясно в детайла — от работния лист (избраното на ръка,
//    след замените).
// Три различни неща, три тона: „без склад" (кехлибарено — липсва настройка, не
// е спешно: станцията няма склад и продуктът няма „Склад по продукт"),
// „недостиг" (червено — реално не стига в избрания склад) и „Всичко е
// налично" (зелено — САМО когато всички продукти имат склад и наличност).
import type { TFunction } from "i18next";
import type { ReportPostingPreview } from "../../../../api/inventory";
import type { PillTone } from "../../components/list/ListBits";
import { isShortByDefaults, lacksDefaultWarehouse } from "./reportPostingDefaults";

// `shortText` — за тясната карта („6 без склад", „3 с недостиг" без „· 1 със
// заместител", „Налично"); пълният `text` е в подсказката
export type PostingStatusPart = { tone: PillTone; text: string; shortText?: string };

export const pendingStatusFromPreview = (
  preview: ReportPostingPreview,
  groupMemberIds: Set<number>,
) => {
  const short = preview.products.filter(isShortByDefaults);
  return {
    noWarehouseCount: preview.products.filter(lacksDefaultWarehouse).length,
    shortCount: short.length,
    withSubCount: short.filter((product) => groupMemberIds.has(product.productId)).length,
  };
};

// Картата в списъка: „без склад" и „с недостиг" могат да са и двете
export const pendingStatusParts = (
  counts: ReturnType<typeof pendingStatusFromPreview>,
  t: TFunction,
): PostingStatusPart[] => {
  const parts: PostingStatusPart[] = [];
  if (counts.noWarehouseCount)
    parts.push({
      tone: "brand",
      text: t("inventoryPage.postingStatusNoWarehouse", { count: counts.noWarehouseCount }),
      shortText: t("inventoryPage.postingStatusNoWarehouseShort", { count: counts.noWarehouseCount }),
    });
  if (counts.shortCount) {
    const withSub = counts.withSubCount
      ? ` · ${t("inventoryPage.postingStatusWithSub", { count: counts.withSubCount })}`
      : "";
    parts.push({
      tone: "danger",
      text: t("inventoryPage.postingStatusShort", { count: counts.shortCount }) + withSub,
      shortText: t("inventoryPage.postingStatusShortShort", { count: counts.shortCount }),
    });
  }
  if (!parts.length)
    parts.push({
      tone: "ok",
      text: t("inventoryPage.postingStatusAllAvailable"),
      shortText: t("inventoryPage.postingStatusAllAvailableShort"),
    });
  return parts;
};

// Хапчетата в детайла — от работния лист; заместените са суфикс на последното
export const detailStatusParts = (
  counts: { noWarehouseCount: number; shortCount: number; substitutedCount: number },
  t: TFunction,
): PostingStatusPart[] => {
  const parts: PostingStatusPart[] = [];
  if (counts.noWarehouseCount)
    parts.push({
      tone: "brand",
      text: t("inventoryPage.postingStatusNoWarehouse", { count: counts.noWarehouseCount }),
    });
  if (counts.shortCount)
    parts.push({
      tone: "danger",
      text: t("inventoryPage.postingChipShort", { count: counts.shortCount }),
    });
  if (!parts.length) parts.push({ tone: "ok", text: t("inventoryPage.postingStatusAllAvailable") });
  if (counts.substitutedCount) {
    const last = parts[parts.length - 1];
    last.text += ` · ${t("inventoryPage.postingChipSubstituted", { count: counts.substitutedCount })}`;
  }
  return parts;
};
